import { Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { type ChangeEvent, useCallback, useContext, useState } from 'react';
import { type Message } from 'types/Message';
import { makeStyles } from '@mui/styles';
import { adminFontFamily } from 'theme/MainTheme';
import { type StateSetter } from 'types/utils/React';
import AdminMessagesTablePagination from './AdminMessagesTablePagination';
import { FormattedMessage } from 'react-intl';
import { AdminMessagesTableDataTypeEnum } from './AdminMessagesTableData';
import { navigateToAdminMessageViewer } from 'utils/history';
import { useSelectedLinkContext } from 'contexts';
import { adminMessagesContext } from '../AdminMessagesContext';
import useSmallDesktopScreen from 'hooks/useSmallDesktopScreen';
import AdminMessagesMobileTable from './AdminMessagesMobileTable';
import AdminMessagesTableRow from './AdminMessagesTableRow';

const ITEMS_PER_PAGE = 10;

const useStyles = makeStyles(() => ({
  attachmentTh: {
    width: 0,
  },
  container: {
    borderRadius: '10px 0 10px 10px',
    padding: 20,
    width: 'calc(100% - 40px)',

    '& td, th': {
      fontFamily: adminFontFamily,
      overflow: 'hidden',
      padding: '10px 5px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  dateTh: {
    width: 140,
  },
  emailAddressTh: {
    width: 0,
  },
}), { name: 'AdminMessagesTable' });

type AdminMessagesTablePureProps = {
  areAllMessagesSelected: boolean;
  currentPage: number;
  emailAddressLabelId: string;
  messages: Message[];
  pageCount: number;
  type: AdminMessagesTableDataTypeEnum;
  handleRowClick: (message: Message) => void;
  handleSelectAllMessages: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  setCurrentPage: StateSetter<number>;
};

const AdminMessagesTablePure = ({
  areAllMessagesSelected,
  currentPage,
  emailAddressLabelId,
  messages,
  pageCount,
  type,
  handleRowClick,
  handleSelectAllMessages,
  setCurrentPage,
}: AdminMessagesTablePureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <TableContainer className={classes.container} component={Paper}>
      <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell className={classes.attachmentTh}>
              {messages.length > 0 && (
                <Checkbox
                  checked={areAllMessagesSelected}
                  onChange={handleSelectAllMessages}
                />
              )}
            </TableCell>

            <TableCell className={classes.emailAddressTh}>
              <FormattedMessage id={emailAddressLabelId} />
            </TableCell>

            <TableCell className={classes.attachmentTh} />

            <TableCell>
              <FormattedMessage id="admin.messages.column.message" />
            </TableCell>

            <TableCell align="right" className={classes.dateTh}>
              <FormattedMessage id="admin.messages.column.date" />
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {messages.map(message => (
            <AdminMessagesTableRow
              key={message.id}
              message={message}
              type={type}
              handleRowClick={handleRowClick}
            />
          ))}
        </TableBody>
      </Table>

      <AdminMessagesTablePagination
        currentPage={currentPage}
        pageCount={pageCount}
        setCurrentPage={setCurrentPage}
      />
    </TableContainer>
  );
};

type GetAreAllMessagesSelectedInput = {
  messages: Message[];
  selectedMessages: Message[];
};

const getAreAllMessagesSelected = ({
  messages,
  selectedMessages,
}: GetAreAllMessagesSelectedInput): boolean =>
  messages.every((message) => selectedMessages.includes(message));

const sortMessagesFn = (a: Message, b: Message) =>
  a?.date > b?.date ? -1 : 1;

const getMessages = (
  isSmallScreen: boolean,
  allMessages: Message[],
  currentPage: number,
) => {
  const sortedMessages = [...allMessages].sort(sortMessagesFn);

  if (isSmallScreen) {
    return sortedMessages;
  }

  const firstElementIndex = currentPage * ITEMS_PER_PAGE;

  return sortedMessages.slice(
    firstElementIndex,
    firstElementIndex + ITEMS_PER_PAGE,
  );
};

type AdminMessagesTableProps = {
  allMessages: Message[];
  type: AdminMessagesTableDataTypeEnum;
};

const AdminMessagesTable = ({
  allMessages,
  type,
}: AdminMessagesTableProps): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const isSmallScreen = useSmallDesktopScreen();
  const { selectedMessages, resetSelectedMessages, setSelectedMessages } =
    useContext(adminMessagesContext);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const messages = getMessages(isSmallScreen, allMessages, currentPage);

  const handleRowClick = useCallback(
    (message: Message) =>
      navigateToAdminMessageViewer(selectedLink, message.key),
    [selectedLink],
  );

  const handleSelectAllMessages = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        setSelectedMessages(messages);
      } else {
        resetSelectedMessages();
      }
    },
    [messages, resetSelectedMessages, setSelectedMessages],
  );

  if (isSmallScreen) {
    return (
      <AdminMessagesMobileTable
        messages={messages}
        type={type}
        handleRowClick={handleRowClick}
      />
    );
  }

  const emailAddressLabelId =
    type === AdminMessagesTableDataTypeEnum.SENT
      ? 'admin.messages.column.recipient'
      : 'admin.messages.column.sender';

  const pageCount = Math.ceil(allMessages.length / ITEMS_PER_PAGE);

  const areAllMessagesSelected = getAreAllMessagesSelected({
    messages,
    selectedMessages,
  });

  return (
    <AdminMessagesTablePure
      areAllMessagesSelected={areAllMessagesSelected}
      currentPage={currentPage}
      emailAddressLabelId={emailAddressLabelId}
      messages={messages}
      pageCount={pageCount}
      type={type}
      handleRowClick={handleRowClick}
      handleSelectAllMessages={handleSelectAllMessages}
      setCurrentPage={setCurrentPage}
    />
  );
};

export default AdminMessagesTable;
